import React, { useEffect, useState, useRef } from "react";

import { addCommasToNum, humanFileSize } from "./utils";

import { useQuery } from "react-query";
import {
  getNumberOfSources,
  getTotalEventsScheduled,
  getLiveAndVodHours,
  getUsers,
} from "./actions";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Grid,
  Skeleton,
  Stack,
} from "@mui/material";

import {
  PieChart,
  Pie,
  Sector,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";

import GaugeChart from "react-gauge-chart";

import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import StreamIcon from "@mui/icons-material/Stream";
import SpatialAudioOffIcon from "@mui/icons-material/SpatialAudioOff";
import ClosedCaptionOutlinedIcon from "@mui/icons-material/ClosedCaptionOutlined";
import EventIcon from "@mui/icons-material/Event";
import QueryBuilderOutlinedIcon from "@mui/icons-material/QueryBuilderOutlined";
import LinearGauge from "./sharedComponents/LinearGauge";

import Tooltip from '@mui/material/Tooltip';
import InfoOutlined from "@mui/icons-material/InfoOutlined";

export const UsageContainer = ({
  title,
  container,
  children,
  defaultWidths,
  order = {},
  cardStyle = {},
  gridStyles = {},
  ...sizes
}) => {
  const defaultSizes = {
    xs: 12,
    md: 6,
    lg: 4,
  };

  const sizingProps = defaultWidths ? { ...defaultSizes } : { ...sizes };

  return (
    <Grid item {...sizingProps} order={order} sx={{ ...gridStyles }}>
      <Card sx={{ ...cardStyle }}>
        {title && (
          <>
            <CardHeader
              title={title}
              titleTypographyProps={{ variant: "h4" }}
            />
            <Divider />
          </>
        )}
        <CardContent>{children}</CardContent>
      </Card>
    </Grid>
  );
};

export const UsageItemWithCount = ({
  icon,
  count,
  description,
  isLoading,
  dropdownMenu,
  addCommas = true,
}) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex">
          <Box sx={{mt: 0.5}}>
          {isLoading ? (
            <Skeleton
              width={30}
              height={30}
              sx={{ mr: 1 }}
              variant="circular"
            />
          ) : (
            icon
          )}
          </Box>


          <Box sx={{ width: "100%" }}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h2" sx={{ fontWeight: 800, ml: 0.12 }}>
                {isLoading ? (
                  <Skeleton width={30} variant="text" />
                ) : addCommas ? (
                  addCommasToNum(count, 3, 0)
                ) : (
                  count
                )}
              </Typography>
              {dropdownMenu}
            </Box>
            <Typography sx={{ opacity: 0.45 }} variant="subtitle1">
              {isLoading ? <Skeleton width={100} /> : description}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const UsersDoughnut = ({ backgroundColor, clientID }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const onPieEnter = (_, index) => setActiveIndex(index);

  const renderActiveShape = (props) => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
    } = props;

    return (
      <g>
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          style={{ fontSize: 28, fontWeight: 800 }}
        >
          {payload.count}
        </text>
        <text
          x={cx}
          y={cy}
          dy={20}
          textAnchor="middle"
          style={{ fontSize: 16, opacity: 0.45 }}
        >
          {payload.typeOfUser}
        </text>

        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
      </g>
    );
  };

  const { isLoading, data: usersInfo } = useQuery(`users`, () =>
    getUsers({ clientID })
  );

  if (isLoading)
    return (
      <>
        <Skeleton width={30} />
        <Skeleton width={100} />

        <Stack sx={{ ml: 1 }}>
          <Skeleton
            variant="circular"
            width={250}
            height={250}
            sx={{ marginInline: "auto" }}
          />
        </Stack>
      </>
    );

  return (
    <Box display="flex">
      <Box>
        <Typography variant="h2" sx={{ fontWeight: 800, ml: 0.12 }}>
          {usersInfo.total}
        </Typography>
        <Typography
          sx={{ opacity: 0.45, width: "max-content" }}
          variant="subtitle1"
        >
          Total Users
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            data={usersInfo.userCategories}
            cx="50%"
            cy="50%"
            innerRadius={90}
            outerRadius={120}
            dataKey="count"
            onMouseEnter={onPieEnter}
          >
            {usersInfo.userCategories.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={backgroundColor[index % backgroundColor.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const PlanBandwidthConsumption = ({
  bandwidthPercentage,
  bandwidthTotal,
  bandwidthOverage,
}) => {
  const [overageAmount] = bandwidthOverage.split(" ");

  return (
    <>
      <Typography variant="h3" sx={{ marginLeft: 0.12 }}>
        Bandwidth Consumption

        <Tooltip sx={{
            fontSize: 14,
        }} title="Estimated account bandwidth usage.  Final bandwidth utilization may vary.">
            <InfoOutlined sx={{ ml: 1, fontSize: 16, color: "grey" }} />
        </Tooltip>
      </Typography>

      <Box sx={{ marginLeft: 0.12 }}>
        <Typography
          variant="subtitle1"
          sx={{
            ...(bandwidthPercentage >= 100
              ? {
                  color: "rgb(219,80,74)",
                  opacity: 1,
                }
              : {
                  opacity: 0.45,
                }),

            display: "inline",
          }}
        >
          {bandwidthPercentage}%
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ display: "inline", opacity: 0.45 }}
        >
          {` | Total: ${bandwidthTotal} | `}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            ...(overageAmount.replace(/,/g, "") > 0
              ? {
                  color: "rgb(219,80,74)",
                  opacity: 1,
                }
              : {
                  opacity: 0.45,
                }),

            display: "inline",
          }}
        >
          Overage: {bandwidthOverage}
        </Typography>
      </Box>

      <LinearGauge
        percentage={bandwidthPercentage > 100 ? 100 : bandwidthPercentage}
        legendTitle="Percentage of Plan"
        tooltipTitle="Percentage of Plan Used"
        over100={bandwidthPercentage > 100}
      />
    </>
  );
};

export const StorageUsage = ({
  storageTotal,
  storagePercentage,
  storageOverage,
  unlimitedStorage,
}) => {
  const [overageAmount] = storageOverage.split(" ");
  return (
    <>
      <Typography variant="h3" sx={{ marginLeft: 0.12 }}>
        Storage Usage
      </Typography>
      <Box sx={{ marginLeft: 0.12 }}>
        {!unlimitedStorage && (
          <Typography
            variant="subtitle1"
            sx={{
              ...(storagePercentage >= 100
                ? {
                    color: "rgb(219,80,74)",
                    opacity: 1,
                  }
                : {
                    opacity: 0.45,
                  }),

              display: "inline",
            }}
          >
            {storagePercentage}%
          </Typography>
        )}
        <Typography
          variant="subtitle1"
          sx={{ display: "inline", opacity: 0.45 }}
        >
          {unlimitedStorage
            ? `Total: ${storageTotal} | Unlimited`
            : ` | Total: ${storageTotal} | `}
        </Typography>
        {!unlimitedStorage && (
          <Typography
            variant="subtitle1"
            sx={{
              ...(overageAmount.replace(/,/g, "") > 0
                ? {
                    color: "rgb(219,80,74)",
                    opacity: 1,
                  }
                : {
                    opacity: 0.45,
                  }),

              display: "inline",
            }}
          >
            Overage: {storageOverage}
          </Typography>
        )}
      </Box>
      <LinearGauge
        percentage={storagePercentage > 100 ? 100 : storagePercentage}
        over100={storagePercentage > 100}
        legendTitle="Percentage of Plan"
        tooltipTitle="Percentage of Plan Used"
        unlimited={unlimitedStorage}
      />
    </>
  );
};

export const LiveAndVODBandwidthConsumption = ({ data }) => {
  /* Make sure there is data to display */
  const liveAndVodIsZero = data[0].val === 0 && data[1].val === 0;

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    type,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <>
      <Typography variant="h3" sx={{ marginLeft: 0.12 }}>
        Live and VOD Bandwidth Consumption
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          marginLeft: 0.12,
          opacity: 0.45,
          // ...(!liveAndVodIsZero && { marginBottom: 1 }),
        }}
      >
        {data.map(
          (el, i) =>
            `${el.type} ${humanFileSize(el.val, true)} ${i === 0 ? " | " : ""}`
        )}
      </Typography>
      {!liveAndVodIsZero && (
        // <Box sx={{ border: "1px solid red" }}>
        <ResponsiveContainer width="100%" height={376}>
          <PieChart>
            <Pie
              data={data}
              dataKey="val"
              nameKey="type"
              cx="50%"
              cy="50%"
              labelLine={false}
              isAnimationActive={false}
              label={renderCustomizedLabel}
              style={{ marginInline: "auto" }}
              // label={(entry) =>
              //   `${entry.type} ${humanFileSize(entry.value, true)}`
              // }
            >
              <Cell fill="#576" />
              <Cell fill="#456" />
            </Pie>
            <Legend />
          </PieChart>
        </ResponsiveContainer>
        // </Box>
      )}
    </>
  );
};

export const TotalSources = ({ clientID }) => {
  const { isLoading, data: numberOfSources } = useQuery(`numberOfSources`, () =>
    getNumberOfSources(clientID)
  );

  return (
    <UsageItemWithCount
      icon={<StreamIcon sx={{ mr: 1 }} />}
      count={numberOfSources}
      description={"Total Sources"}
      isLoading={isLoading}
    />
  );
};

export const TotalEventsScheduled = ({ clientID }) => {
  const { isLoading, data: totalEventsScheduled } = useQuery(
    `totalEventsScheduled`,
    () => getTotalEventsScheduled(clientID)
  );

  return (
    <UsageItemWithCount
      icon={<EventIcon sx={{ mr: 1 }} />}
      count={totalEventsScheduled}
      description={"Events Scheduled"}
      isLoading={isLoading}
    />
  );
};

export const TranscriptionMinutes = ({ minutes, isLoading }) => {
  return (
    <UsageItemWithCount
      icon={<SpatialAudioOffIcon sx={{ mr: 1 }} />}
      count={minutes}
      description="Transcription Minutes"
      isLoading={isLoading}
      addCommas={false}
    />
  );
};

export const LiveMinutes = ({ minutes, isLoading }) => {
  return (
    <UsageItemWithCount
      icon={<ClosedCaptionOutlinedIcon sx={{ mr: 1 }} />}
      count={minutes}
      description="Live Captioning Minutes"
      isLoading={isLoading}
      addCommas={false}
    />
  );
};

export const LiveHoursStreamed = ({ clientID, startDate, endDate }) => {
  const {
    isLoading,
    data: viewingHours = { live: 0 },
    refetch,
  } = useQuery({
    queryKey: `totalHoursProduced`,
    queryFn: () =>
      getLiveAndVodHours({ clientID, startDate, endDate }),
  });

  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    refetch();
  }, [startDate, endDate]);

  return (
    <UsageItemWithCount
      icon={<DynamicFeedIcon sx={{ mr: 1 }} />}
      count={viewingHours.live.toFixed(2)}
      description="Live Hours Streamed"
      isLoading={isLoading}
    />
  );
};

export const VodHoursPublished = ({ clientID, startDate, endDate }) => {
  const {
    isLoading,
    data: viewingHours = { vod: 0 },
    refetch,
  } = useQuery({
    queryKey: `totalHoursProduced`,
    queryFn: () =>
      getLiveAndVodHours({ clientID, startDate, endDate }),
  });

  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    refetch();
  }, [startDate, endDate]);

  return (
    <UsageItemWithCount
      icon={<QueryBuilderOutlinedIcon sx={{ mr: 1 }} />}
      count={viewingHours.vod.toFixed(2)}
      description="VOD Hours Published"
      isLoading={isLoading}
    />
  );
};
